import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getStsToken as requestStsToken, type OssStsVO, type UploadRequestConfig } from '@/api/modules/resource/system';
import { toast } from 'sonner';
import { ApiBusinessError, parseUserError } from '@/utils/userError';
import { APP_CONFIG } from '@/config/env';
import { addMsgOnReloaded } from '@/utils/msgOnReload';

/**
 * STS Token 存储格式
 */
export interface StsToken {
  CurrentTime: string;
  ExpiredTime: string;
  SessionToken: string;
  AccessKeyId: string;
  SecretAccessKey: string;
}

/**
 * STS Token 状态接口
 */
export interface StsState {
  stsToken: StsToken | null;
  isLoading: boolean;
  setStsToken: (token: StsToken) => void;
  getStsToken: (forceRefresh?: boolean, config?: UploadRequestConfig) => Promise<StsToken>;
  refreshStsToken: (expiredToken: StsToken, config?: UploadRequestConfig) => Promise<StsToken>;
  clearStsToken: () => void;
}

/**
 * 将服务端返回的 OssStsVO 转换为 StsToken 格式
 */
const convertToStsToken = (vo: OssStsVO): StsToken => ({
  CurrentTime: vo.currentTime,
  ExpiredTime: vo.expiration,
  SessionToken: vo.securityToken,
  AccessKeyId: vo.accessKeyId,
  SecretAccessKey: vo.accessKeySecret,
});

/**
 * 检查 Token 是否过期
 * @param expiredTime 过期时间字符串
 * @returns true 表示已过期，false 表示未过期
 */
const isTokenExpired = (expiredTime: string): boolean => {
  const expiredTimestamp = new Date(expiredTime).getTime();
  if (Number.isNaN(expiredTimestamp)) return true;

  // 提前 5 分钟刷新，避免临界状态
  const bufferTime = 5 * 60 * 1000;
  return Date.now() >= expiredTimestamp - bufferTime;
};

/**
 * 当前正在执行的 STS 凭证请求
 * 多文件同时开始上传或同时触发过期重试时复用同一个请求，避免重复刷新
 */
interface StsTokenRequest {
  promise: Promise<StsToken>;
  showErrorToast: boolean;
  fallbackNotified: boolean;
}

let stsTokenRequest: StsTokenRequest | null = null;

/** 静默入口先发起共享请求时，后加入的默认入口仍保留一次通知，不额外获取凭证 */
const awaitSharedStsToken = async (pending: StsTokenRequest, config?: UploadRequestConfig) => {
  try {
    return await pending.promise;
  } catch (error) {
    if (!pending.showErrorToast && config?.showErrorToast !== false && !pending.fallbackNotified) {
      const parsed = parseUserError(error, {
        context: config?.errorContext,
        online: typeof navigator === 'undefined' ? undefined : navigator.onLine,
      });
      if (parsed.message) {
        pending.fallbackNotified = true;
        // 与公共请求层保持一致：非开发环境的会话失效提示在跳转后显示
        if (error instanceof ApiBusinessError && error.code === 401 && !APP_CONFIG.isDev) {
          addMsgOnReloaded({ type: 'error', content: parsed.message });
        } else {
          toast.error(parsed.message, { duration: 3000 });
        }
      }
    }
    throw error;
  }
};

/**
 * STS Token 状态管理 Store
 * 使用 Zustand 创建轻量级状态管理
 * 支持状态持久化到 localStorage
 * 自动判断过期并刷新 Token
 */
export const useStsStore = create<StsState>()(
  persist(
    (set, get) => ({
      // 初始状态
      stsToken: null,
      isLoading: false,

      // 设置 STS Token
      setStsToken: (token: StsToken) => {
        set({ stsToken: token });
      },

      /**
       * 获取 STS Token
       * 默认复用未过期凭证，提示配置不改变缓存和并发请求复用
       */
      getStsToken: async (forceRefresh = false, config) => {
        const { stsToken } = get();

        if (!forceRefresh && stsToken && !isTokenExpired(stsToken.ExpiredTime)) {
          return stsToken;
        }

        if (stsTokenRequest) return awaitSharedStsToken(stsTokenRequest, config);

        if (forceRefresh) {
          set({ stsToken: null, isLoading: true });
        } else {
          set({ isLoading: true });
        }

        const request = (async () => {
          const response = await requestStsToken(config);
          if (!response.data) {
            throw new Error(response.msg);
          }
          const newToken = convertToStsToken(response.data);
          set({ stsToken: newToken });
          return newToken;
        })();
        stsTokenRequest = {
          promise: request,
          showErrorToast: config?.showErrorToast !== false,
          fallbackNotified: false,
        };

        try {
          return await request;
        } finally {
          stsTokenRequest = null;
          set({ isLoading: false });
        }
      },

      /**
       * 刷新上传过程中失效的 STS Token
       * 多文件可能先后上报同一份凭证过期，若其他文件已完成刷新则直接复用最新凭证
       * 提示配置仅传递给请求层，不改变刷新与共享规则
       */
      refreshStsToken: async (expiredToken, config) => {
        const currentToken = get().stsToken;
        const hasBeenRefreshed =
          currentToken &&
          (currentToken.AccessKeyId !== expiredToken.AccessKeyId ||
            currentToken.SessionToken !== expiredToken.SessionToken);
        if (hasBeenRefreshed && !isTokenExpired(currentToken.ExpiredTime)) {
          return currentToken;
        }
        return get().getStsToken(true, config);
      },

      // 清除 STS Token
      clearStsToken: () => {
        set({ stsToken: null });
      },
    }),
    {
      name: 'sts-storage', // localStorage 中的 key
    }
  )
);

/**
 * 获取 STS Token 的便捷方法
 * @returns Promise<StsToken>
 */
export const fetchStsToken = () => useStsStore.getState().getStsToken();

/**
 * 清除 STS Token 的便捷方法
 */
export const clearStsToken = () => useStsStore.getState().clearStsToken();
